import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/shared/utils/supabase'
import { useAuth } from '@/modules/auth'
import { PageLayout } from '@/ui/layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, WaveLoader } from '@/ui/components'

type VerificationState = 'pending' | 'success' | 'error' | 'instructions'
interface SignupRedirectState {
  pendingEmail?: string
  confirmationMessage?: string
}

export default function VerifyEmail() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, completeEmailVerification } = useAuth()
  const [status, setStatus] = useState<VerificationState>('pending')
  const [errorMessage, setErrorMessage] = useState('Something went wrong with the verification link.')
  const signupState = (location.state as SignupRedirectState | null) ?? null
  const confirmationMessage =
    signupState?.confirmationMessage ??
    'Open the confirmation email and tap the link to verify your address.'

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
      return
    }

    // Check for error parameters in the URL hash (Supabase redirects errors in hash)
    const hash = window.location.hash
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1)) // Remove '#'
      const error = hashParams.get('error')
      const errorCode = hashParams.get('error_code')
      const errorDescription = hashParams.get('error_description')
      
      if (error || errorCode) {
        setStatus('error')
        // Provide user-friendly error messages
        if (errorCode === 'otp_expired' || errorCode === 'token_expired') {
          setErrorMessage('This verification link has expired. Please sign up again to receive a new verification email.')
        } else if (errorDescription) {
          setErrorMessage(decodeURIComponent(errorDescription))
        } else if (error) {
          setErrorMessage(decodeURIComponent(error))
        } else {
          setErrorMessage('Verification link is invalid or has expired. Please request a new one.')
        }
        return
      }
    }

    // Check for token parameters in query string (for manual verification flow)
    const tokenHash = searchParams.get('token_hash') || searchParams.get('token')
    const email = searchParams.get('email')
    const type = searchParams.get('type') ?? 'signup'

    const verifyEmail = async () => {
      try {
        // First, check if Supabase has already created a session automatically
        // (Supabase may set the session when redirecting with tokens in hash)
        let { data: initialSessionData, error: initialSessionError } = await supabase.auth.getSession()

        // If we have a valid session with confirmed email, use it
        if (initialSessionData?.session?.user && initialSessionData.session.user.email_confirmed_at) {
          await completeEmailVerification(initialSessionData.session, initialSessionData.session.user)
          setStatus('success')
          // Clean up the hash from URL
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
          setTimeout(() => {
            navigate('/onboarding', { replace: true })
          }, 1200)
          return
        }

        // Check URL hash for Supabase tokens (Supabase may include them in hash after redirect)
        const hash = window.location.hash

        if (hash) {
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          
          if (accessToken && refreshToken) {
            // Supabase included tokens in the hash - use them to create a session
            try {
              const { data: newSessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              })
              
              if (newSessionData?.session?.user && !sessionError) {
                await completeEmailVerification(newSessionData.session, newSessionData.session.user)
                setStatus('success')
                // Clean up the hash from URL
                window.history.replaceState(null, '', window.location.pathname + window.location.search)
                setTimeout(() => {
                  navigate('/onboarding', { replace: true })
                }, 1200)
                return
              }
            } catch (hashError) {
              // Continue with normal verification flow
            }
          }
        }
        
        // Wait a moment and check session again (in case Supabase set it asynchronously)
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Check if Supabase has created a session after the delay
        const { data: delayedSessionData, error: delayedSessionError } = await supabase.auth.getSession()
        
        if (delayedSessionData?.session?.user) {
          // Check if email is confirmed
          const isEmailConfirmed = delayedSessionData.session.user.email_confirmed_at !== null
          
          if (isEmailConfirmed) {
            // Session exists and email is confirmed - Supabase processed it successfully
            await completeEmailVerification(delayedSessionData.session, delayedSessionData.session.user)
            setStatus('success')
            setTimeout(() => {
              navigate('/onboarding', { replace: true })
            }, 1200)
            return
          }
        }

        // If no session exists or email not confirmed, try to verify with the token
        // Only attempt verifyOtp if we have a tokenHash (from query params)
        if (!tokenHash) {
          // No token in query params and no session - check if we should show instructions
          if (signupState?.pendingEmail) {
            setStatus('instructions')
            return
          }
          
          // No token and no session - this shouldn't happen if Supabase redirected properly
          setStatus('error')
          setErrorMessage('Verification link is invalid or has expired. Please request a new one.')
          return
        }

        // Supabase's verifyOtp can work without email if the token is valid

        try {
          const verifyOptions: any = {
            token_hash: tokenHash,
            type: type === 'invite' ? 'invite' : 'signup'
          }
          
          // Only include email if provided (Supabase can verify without it)
          if (email) {
            verifyOptions.email = email
          }

          const { data, error } = await supabase.auth.verifyOtp(verifyOptions)

          if (error) {
            // If verifyOtp fails, check if it's because the token was already used
            // (which means Supabase already processed it)
            const isAlreadyVerified = error.message?.includes('already been verified') || 
                error.message?.includes('already confirmed') ||
                error.message?.includes('Token has already been used')

            if (isAlreadyVerified) {
              // Token was already used - check session again
              const { data: retrySession } = await supabase.auth.getSession()

              if (retrySession?.session?.user) {
                await completeEmailVerification(retrySession.session, retrySession.session.user)
                setStatus('success')
                setTimeout(() => {
                  navigate('/onboarding', { replace: true })
                }, 1200)
                return
              }
            }
            
            setStatus('error')
            // Provide more helpful error messages
            if (error?.message?.includes('expired') || error?.message?.includes('invalid')) {
              setErrorMessage('This verification link has expired. Please sign up again to receive a new verification email.')
            } else {
              setErrorMessage(error?.message ?? 'Unable to verify your email. Please request a new link.')
            }
            return
          }

          if (data?.session && data.user) {
          await completeEmailVerification(data.session, data.user)
          setStatus('success')

          setTimeout(() => {
            navigate('/onboarding', { replace: true })
          }, 1200)
        } else {
            setStatus('error')
            setErrorMessage('Verification completed but no session was created. Please try signing in.')
          }
        } catch (verifyError) {
          // If verifyOtp throws an error, check session one more time
          // (Supabase might have processed it in the background)
          const { data: finalSession } = await supabase.auth.getSession()

          if (finalSession?.session?.user && finalSession.session.user.email_confirmed_at) {
            await completeEmailVerification(finalSession.session, finalSession.session.user)
            setStatus('success')
            setTimeout(() => {
              navigate('/onboarding', { replace: true })
            }, 1200)
          } else {
          setStatus('error')
            setErrorMessage(verifyError instanceof Error ? verifyError.message : 'Verification failed. Please try again.')
          }
        }
      } catch (err) {
        setStatus('error')
        setErrorMessage(err instanceof Error ? err.message : 'Verification failed. Please try again.')
      }
    }

    verifyEmail()
  }, [searchParams, navigate, isAuthenticated, completeEmailVerification, signupState?.pendingEmail])

  const renderContent = () => {
    if (status === 'instructions') {
      return (
        <div className="space-y-6">
          <div className="rounded-xl border border-adventure-200 bg-adventure-50 px-4 py-5 text-sm text-adventure-900">
            <p className="font-semibold text-base">Check your inbox</p>
            <p className="mt-2">{confirmationMessage}</p>
            {signupState?.pendingEmail && (
              <p className="mt-3 text-xs text-muted-foreground">
                Sent to <span className="font-medium text-foreground">{signupState.pendingEmail}</span>. It may take up
                to a minute to arrive.
              </p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              Already confirmed?{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                Sign in
              </Link>
            </p>
          </div>
          <div className="rounded-xl border border-earth-200 bg-earth-50 px-4 py-5 text-sm text-earth-700">
            <p className="font-medium text-earth-900 mb-2">Trouble finding it?</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-earth-700">
              <li>Check spam or promotions folders</li>
              <li>Add support@sitrepp.io to your contacts</li>
              <li>Submit the form again if you need a new link</li>
            </ul>
          </div>
          <Button asChild className="w-full" variant="secondary">
            <a href="mailto:support@sitrepp.io">Contact Support</a>
          </Button>
        </div>
      )
    }

    if (status === 'pending') {
      return (
        <div className="flex flex-col items-center space-y-4 py-8 text-center">
          <WaveLoader />
          <div>
            <p className="text-base font-medium text-foreground">Verifying your account…</p>
            <p className="text-sm text-muted-foreground mt-1">This only takes a moment.</p>
          </div>
        </div>
      )
    }

    if (status === 'success') {
      return (
        <div className="flex flex-col items-center space-y-4 py-8 text-center">
          <div className="text-4xl">✅</div>
          <div>
            <p className="text-base font-medium text-foreground">Email verified</p>
            <p className="text-sm text-muted-foreground mt-1">We’re taking you to onboarding.</p>
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col space-y-4 py-4">
        <p className="text-sm text-destructive">{errorMessage}</p>
        <Button variant="secondary" onClick={() => navigate('/signup', { replace: true })}>
          Back to sign up
        </Button>
      </div>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle>Email verification</CardTitle>
              <CardDescription>Completing your SitRepp account setup.</CardDescription>
            </CardHeader>
            <CardContent>{renderContent()}</CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  )
}

